import type { PublishedPost } from '../types/landing.types'

/**
 * Cliente de `GET /api/posts/published` (endpoint público, sin autenticación).
 *
 * En desarrollo la ruta `/api` la redirige el proxy de Vite al backend
 * (ver `vite.config.ts`); en producción el SPA lo sirve el propio API, así que
 * la misma ruta relativa funciona en ambos entornos.
 */
const POSTS_ENDPOINT = '/api/posts/published'

/** Envoltura paginada que devuelve `ToPagedResult` en el backend. */
interface PagedEnvelope<T> {
  success?: boolean
  message?: string
  statusCode?: number
  data?: T[] | null
  count?: number
  skip?: number
  limit?: number
}

interface PublishedPostDto {
  id: string
  title: string
  description: string
  imageUrl?: string | null
  isPublished?: boolean
  createdAt: string
}

/**
 * Descarga las publicaciones marcadas como publicadas.
 *
 * @param limit Cantidad máxima de publicaciones a traer.
 * @param signal Permite cancelar la petición (p. ej. al desmontar la vista).
 * @throws Error de red o de estado HTTP distinto de 2xx.
 */
export async function fetchPublishedPosts(
  limit = 6,
  signal?: AbortSignal
): Promise<PublishedPost[]> {
  const response = await fetch(`${POSTS_ENDPOINT}?limit=${limit}`, {
    signal,
    headers: { Accept: 'application/json' }
  })

  if (!response.ok) {
    throw new Error(`GET ${POSTS_ENDPOINT} respondió ${response.status}`)
  }

  const payload = (await response.json()) as PagedEnvelope<PublishedPostDto>
  const rows = Array.isArray(payload?.data) ? payload.data : []

  return rows
    .filter((row): row is PublishedPostDto => Boolean(row?.id))
    .map((row) => ({
      id: String(row.id),
      title: row.title ?? '',
      description: row.description ?? '',
      imageUrl: row.imageUrl ?? null,
      isPublished: Boolean(row.isPublished),
      createdAt: row.createdAt ?? ''
    }))
}
